
import ByteView from 'byteview'
import {
  languageAlias,
  variantAlias,
  scriptAlias,
  territoryAlias
} from './data/alias.js'
import likelySubtags from './data/likelySubtags.js'
import timeZones, { timeZoneNames } from './data/timeZones.js'
import hourCycles from './data/hourCycles.js'
import calendars, { calendarNames } from './data/calendars.js'
import weekData from './data/weekData.js'
// import characterOrders from './data/characterOrders.js'
import { numberingSystemNames } from './data/numberingSystems.js'
import { units } from './data/units.js'
import { collations } from './data/collations.js'
// import DateTimeFormat from './DateTimeFormat.js'
// import ISODate from './ISODate.js'
import { currencies } from './data/currencies.js'
import Integer from './Integer.js'
import Locale from './Locale.js'
// import { ObjectCache } from './TimeZone.js'
// import { getType, hashObject } from './utils.js'

export const ALPHANUM_1_8 = /^[a-z0-9]{1,8}$/i
export const ALPHANUM_2_8 = /^[a-z0-9]{2,8}$/i
export const ALPHANUM_3_8 = /^[a-z0-9]{3,8}$/i

export const KEY_REGEX = /^[a-z0-9][a-z]$/i

export const TYPE_REGEX = /^[a-z0-9]{3,8}$/i
export const ALPHA_4 = /^[a-z]{4}$/i
// alphanum-[tTuUxX]
export const OTHER_EXTENSION_TYPE = /^[0-9a-svwyz]$/i
export const UNICODE_REGION_SUBTAG_REGEX = /^([a-z]{2}|[0-9]{3})$/i
export const UNICODE_VARIANT_SUBTAG_REGEX = /^([a-z0-9]{5,8}|[0-9][a-z0-9]{3})$/i
export const UNICODE_LANGUAGE_SUBTAG_REGEX = /^([a-z]{2,3}|[a-z]{5,8})$/i
export const TKEY_REGEX = /^[a-z][0-9]$/i
export const UNICODE_EXTENSION_SEQUENCE_REGEX = /-u(?:-[0-9a-z]{2,8})+/gi
export const SEPARATOR = '-'
export const RELEVANT_EXTENSION_KEYS = ['ca', 'co', 'hc', 'kf', 'kn', 'nu']
export const UNICODE_TYPE_REGEX = /^[a-z0-9]{3,8}(-[a-z0-9]{3,8})*$/i

export function isObject (value) {
  return (typeof value === 'object' && value !== null) || typeof value === 'function'
}

export function isUndefined (value) {
  return value === undefined
}

export function isNumber (value) {
  return (
    typeof value === 'number' &&
    Number.isFinite(value) &&
    !Number.isNaN(value)
  )
}

export function isInteger (value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return false
  const abs = Math.abs(value)
  return Math.floor(abs) === abs
}

export function isLiteralPart (
  patternPart
) {
  return patternPart.type === 'literal'
}

/**
 * https://tc39.es/ecma402/#sec-partitionpattern
 * @param pattern
 */
export function partitionPattern (
  pattern
) {
  const result = []
  let beginIndex = pattern.indexOf('{')
  let endIndex = 0
  let nextIndex = 0
  const length = pattern.length
  while (beginIndex < pattern.length && beginIndex > -1) {
    endIndex = pattern.indexOf('}', beginIndex)
    invariant(endIndex > beginIndex, `Invalid pattern ${pattern}`)
    if (beginIndex > nextIndex) {
      result.push({
        type: 'literal',
        value: pattern.substring(nextIndex, beginIndex),
      })
    }
    result.push({
      type: pattern.substring(beginIndex + 1, endIndex),
      value: undefined,
    })
    nextIndex = endIndex + 1
    beginIndex = pattern.indexOf('{', nextIndex)
  }
  if (nextIndex < length) {
    result.push({
      type: 'literal',
      value: pattern.substring(nextIndex, length),
    })
  }
  return result
}

export function unpack (data) {
  const abbrvs = data.abbrvs.split('|')
  const offsets = data.offsets.split('|').map(n => parseInt(n, 36))
  const packedZones = data.zones
  const zones = {}
  for (const d of packedZones) {
    const [zone, ...zoneData] = d.split('|')

    zones[zone] = zoneData
      .map(z => z.split(','))
      .map(([ts, abbrvIndex, offsetIndex, dst]) => [
        ts === '' ? -Infinity : parseInt(ts, 36),
        abbrvs[+abbrvIndex],
        offsets[+offsetIndex],
        dst === '1'
      ])
  }
  return zones
}

export function getStringOrBooleanOption (
  opts,
  prop,
  values,
  trueValue,
  falsyValue,
  fallback
) {
  let value = opts[prop]
  if (value === undefined) {
    return fallback
  }
  if (value === true) {
    return trueValue
  }
  const valueBoolean = Boolean(value)

  if (valueBoolean === false) {
    return falsyValue
  }

  value = String(value)

  if (value === 'true' || value === 'false') {
    return fallback
  }

  if ((values || []).indexOf(value) === -1) {
    throw new RangeError(`Invalid value ${value}`)
  }

  return value
}

export function isUnicodeLanguageSubtag (lang) {
  return UNICODE_LANGUAGE_SUBTAG_REGEX.test(lang)
}

export function isStructurallyValidLanguageTag (tag) {
  try {
    parseUnicodeLanguageId(tag.split(SEPARATOR))
  } catch (e) {
    return false
  }
  return true
}

export function isUnicodeRegionSubtag (region) {
  return UNICODE_REGION_SUBTAG_REGEX.test(region)
}

export function isUnicodeScriptSubtag (script) {
  return ALPHA_4.test(script)
}

export function isUnicodeVariantSubtag (variant) {
  return UNICODE_VARIANT_SUBTAG_REGEX.test(variant)
}

export function parseUnicodeLanguageId (chunks) {
  if (typeof chunks === 'string') {
    chunks = chunks.split(SEPARATOR)
  }
  const lang = chunks.shift()
  if (!lang) {
    throw new RangeError('Missing unicode_language_subtag')
  }
  if (lang === 'root') {
    return { lang: 'root', variants: [] }
  }
  // unicode_language_subtag
  if (!isUnicodeLanguageSubtag(lang)) {
    throw new RangeError('Malformed unicode_language_subtag')
  }
  let script
  // unicode_script_subtag
  if (chunks.length && isUnicodeScriptSubtag(chunks[0])) {
    script = chunks.shift()
  }
  let region
  // unicode_region_subtag
  if (chunks.length && isUnicodeRegionSubtag(chunks[0])) {
    region = chunks.shift()
  }
  const variants = {}
  while (chunks.length && isUnicodeVariantSubtag(chunks[0])) {
    const variant = chunks.shift()
    if (variant in variants) {
      throw new RangeError(`Duplicate variant "${variant}"`)
    }
    variants[variant] = 1
  }
  return {
    lang,
    script,
    region,
    variants: Object.keys(variants)
  }
}

export function parseUnicodeExtension (chunks) {
  const keywords = []
  let keyword
  while (chunks.length && (keyword = parseKeyword(chunks))) {
    keywords.push(keyword)
  }
  if (keywords.length) {
    return {
      type: 'u',
      keywords,
      attributes: []
    }
  }
  // Mix of attributes & keywords
  // Check for attributes first
  const attributes = []
  while (chunks.length && ALPHANUM_3_8.test(chunks[0])) {
    attributes.push(chunks.shift())
  }
  while (chunks.length && (keyword = parseKeyword(chunks))) {
    keywords.push(keyword)
  }
  if (keywords.length || attributes.length) {
    return {
      type: 'u',
      attributes,
      keywords
    }
  }
  throw new RangeError('Malformed unicode_extension')
}

export function parseKeyword (chunks) {
  let key
  if (!KEY_REGEX.test(chunks[0])) {
    return
  }
  key = chunks.shift()

  const type = []
  while (chunks.length && TYPE_REGEX.test(chunks[0])) {
    type.push(chunks.shift())
  }
  let value = ''
  if (type.length) {
    value = type.join(SEPARATOR)
  }
  return [key, value]
}

export function parseTransformedExtension (chunks) {
  let lang
  try {
    lang = parseUnicodeLanguageId(chunks)
  } catch (e) {
    // Try just parsing tfield
  }
  const fields = []
  while (chunks.length && TKEY_REGEX.test(chunks[0])) {
    const key = chunks.shift()
    const value = []
    while (chunks.length && ALPHANUM_3_8.test(chunks[0])) {
      value.push(chunks.shift())
    }
    if (!value.length) {
      throw new RangeError(`Missing tvalue for tkey "${key}"`)
    }
    fields.push([key, value.join(SEPARATOR)])
  }
  if (fields.length) {
    return {
      type: 't',
      fields,
      lang
    }
  }
  throw new RangeError('Malformed transformed_extension')
}

export function parsePuExtension (chunks) {
  const exts = []
  while (chunks.length && ALPHANUM_1_8.test(chunks[0])) {
    exts.push(chunks.shift())
  }
  if (exts.length) {
    return {
      type: 'x',
      value: exts.join(SEPARATOR)
    }
  }
  throw new RangeError('Malformed private_use_extension')
}

export function parseOtherExtensionValue (chunks) {
  const exts = []
  while (chunks.length && ALPHANUM_2_8.test(chunks[0])) {
    exts.push(chunks.shift())
  }
  if (exts.length) {
    return exts.join(SEPARATOR)
  }
  return ''
}

export function parseExtensions (chunks) {
  if (!chunks.length) {
    return { extensions: [] }
  }
  const extensions = []
  let unicodeExtension
  let transformedExtension
  let puExtension
  const otherExtensionMap = {}
  do {
    const type = chunks.shift()
    switch (type) {
      case 'u':
      case 'U':
        if (unicodeExtension) {
          throw new RangeError('There can only be 1 -u- extension')
        }
        unicodeExtension = parseUnicodeExtension(chunks)
        extensions.push(unicodeExtension)
        break
      case 't':
      case 'T':
        if (transformedExtension) {
          throw new RangeError('There can only be 1 -t- extension')
        }
        transformedExtension = parseTransformedExtension(chunks)
        extensions.push(transformedExtension)
        break
      case 'x':
      case 'X':
        if (puExtension) {
          throw new RangeError('There can only be 1 -x- extension')
        }
        puExtension = parsePuExtension(chunks)
        extensions.push(puExtension)
        break

      default: {
        if (!OTHER_EXTENSION_TYPE.test(type)) {
          throw new RangeError('Malformed extension type')
        }
        if (type in otherExtensionMap) {
          throw new RangeError(`There can only be 1 -${type}- extension`)
        }
        const extension = {
          type,
          value: parseOtherExtensionValue(chunks)
        }
        otherExtensionMap[extension.type] = extension
        extensions.push(extension)
        break
      }
    }
  } while (chunks.length)
  return { extensions }
}

export function parseUnicodeLocaleId (locale) {
  const chunks = locale.split(SEPARATOR)
  const lang = parseUnicodeLanguageId(chunks)
  return {
    lang,
    ...parseExtensions(chunks)
  }
}

export function canonicalizeAttrs (strs) {
  return Object.keys(
    strs.reduce((all, str) => {
      all[str.toLowerCase()] = 1
      return all
    }, {})
  ).sort()
}

export function canonicalizeKVs (arr) {
  const all = {}
  const result = []
  for (const kv of arr) {
    if (kv[0] in all) {
      continue
    }
    all[kv[0]] = 1
    if (!kv[1] || kv[1] === 'true') {
      result.push([kv[0].toLowerCase()])
    } else {
      result.push([kv[0].toLowerCase(), kv[1].toLowerCase()])
    }
  }
  return result.sort(compareKV)
}

export function compareKV (t1, t2) {
  return t1[0] < t2[0] ? -1 : t1[0] > t2[0] ? 1 : 0
}

export function compareExtension (e1, e2) {
  return e1.type < e2.type ? -1 : e1.type > e2.type ? 1 : 0
}

export function mergeVariants (v1, v2) {
  const result = [...v1]
  for (const v of v2) {
    if (v1.indexOf(v) < 0) {
      result.push(v)
    }
  }
  return result
}

/**
 * CAVEAT: We don't do this section in the spec bc they have no JSON data
 * Use the bcp47 data to replace keys, types, tfields, and tvalues by their canonical forms. See Section 3.6.4 U Extension Data Files) and Section 3.7.1 T Extension Data Files. The aliases are in the alias attribute value, while the canonical is in the name attribute value. For example,
Because of the following bcp47 data:
<key name="ms"…>…<type name="uksystem" … alias="imperial" … />…</key>
We get the following transformation:
en-u-ms-imperial ⇒ en-u-ms-uksystem
 * @param lang
 */
export function canonicalizeUnicodeLanguageId (unicodeLanguageId) {
  /**
   * If the language subtag matches the type attribute of a languageAlias element in Supplemental Data, replace the language subtag with the replacement value.
   *  1. If there are additional subtags in the replacement value, add them to the result, but only if there is no corresponding subtag already in the tag.
   *  2. Five special deprecated grandfathered codes (such as i-default) are in type attributes, and are also replaced.
   */

  // From https://github.com/unicode-org/icu/blob/master/icu4j/main/classes/core/src/com/ibm/icu/util/ULocale.java#L1246

  // Try language _ variant
  let finalLangAst = unicodeLanguageId
  if (unicodeLanguageId.variants.length) {
    let replacedLang = ''
    for (const variant of unicodeLanguageId.variants) {
      if (
        (replacedLang =
          languageAlias[
            emitUnicodeLanguageId({
              lang: unicodeLanguageId.lang,
              variants: [variant]
            })
          ])
      ) {
        const replacedLangAst = parseUnicodeLanguageId(
          replacedLang.split(SEPARATOR)
        )
        finalLangAst = {
          lang: replacedLangAst.lang,
          script: finalLangAst.script || replacedLangAst.script,
          region: finalLangAst.region || replacedLangAst.region,
          variants: mergeVariants(
            finalLangAst.variants,
            replacedLangAst.variants
          )
        }
        break
      }
    }
  }

  // language _ script _ country
  // ug-Arab-CN -> ug-CN
  if (finalLangAst.script && finalLangAst.region) {
    const replacedLang =
      languageAlias[
        emitUnicodeLanguageId({
          lang: finalLangAst.lang,
          script: finalLangAst.script,
          region: finalLangAst.region,
          variants: []
        })
      ]
    if (replacedLang) {
      const replacedLangAst = parseUnicodeLanguageId(
        replacedLang.split(SEPARATOR)
      )
      finalLangAst = {
        lang: replacedLangAst.lang,
        script: replacedLangAst.script,
        region: replacedLangAst.region,
        variants: finalLangAst.variants
      }
    }
  }

  // language _ country
  // eg. az_AZ -> az_Latn_A
  if (finalLangAst.region) {
    const replacedLang =
      languageAlias[
        emitUnicodeLanguageId({
          lang: finalLangAst.lang,
          region: finalLangAst.region,
          variants: []
        })
      ]
    if (replacedLang) {
      const replacedLangAst = parseUnicodeLanguageId(
        replacedLang.split(SEPARATOR)
      )
      finalLangAst = {
        lang: replacedLangAst.lang,
        script: finalLangAst.script || replacedLangAst.script,
        region: replacedLangAst.region,
        variants: finalLangAst.variants
      }
    }
  }
  // only language
  // e.g. twi -> ak
  const replacedLang =
    languageAlias[
      emitUnicodeLanguageId({
        lang: finalLangAst.lang,
        variants: []
      })
    ]
  if (replacedLang) {
    const replacedLangAst = parseUnicodeLanguageId(
      replacedLang.split(SEPARATOR)
    )
    finalLangAst = {
      lang: replacedLangAst.lang,
      script: finalLangAst.script || replacedLangAst.script,
      region: finalLangAst.region || replacedLangAst.region,
      variants: finalLangAst.variants
    }
  }

  if (finalLangAst.region) {
    const region = finalLangAst.region.toUpperCase()
    const regionAlias = territoryAlias[region]
    let replacedRegion
    if (regionAlias) {
      const regions = regionAlias.split(' ')
      replacedRegion = regions[0]
      const likelySubtag =
        likelySubtags[
          emitUnicodeLanguageId({
            lang: finalLangAst.lang,
            script: finalLangAst.script,
            variants: []
          })
        ]
      if (likelySubtag) {
        const { region: likelyRegion } = parseUnicodeLanguageId(
          likelySubtag.split(SEPARATOR)
        )
        if (likelyRegion && regions.indexOf(likelyRegion) > -1) {
          replacedRegion = likelyRegion
        }
      }
    }
    if (replacedRegion) {
      finalLangAst.region = replacedRegion
    }
    finalLangAst.region = finalLangAst.region.toUpperCase()
  }
  if (finalLangAst.script) {
    finalLangAst.script =
      finalLangAst.script[0].toUpperCase() +
      finalLangAst.script.slice(1).toLowerCase()
    if (scriptAlias[finalLangAst.script]) {
      finalLangAst.script = scriptAlias[finalLangAst.script]
    }
  }

  if (finalLangAst.variants.length) {
    for (let i = 0; i < finalLangAst.variants.length; i++) {
      let variant = finalLangAst.variants[i].toLowerCase()
      if (variantAlias[variant]) {
        const alias = variantAlias[variant]
        if (isUnicodeVariantSubtag(alias)) {
          finalLangAst.variants[i] = alias
        } else if (isUnicodeLanguageSubtag(alias)) {
          // Yes this can happen per the spec
          finalLangAst.lang = alias
        }
      }
    }
    finalLangAst.variants.sort()
  }
  return finalLangAst
}

/**
 * Canonicalize based on
 * https://www.unicode.org/reports/tr35/tr35.html#Canonical_Unicode_Locale_Identifiers
 * https://tc39.es/ecma402/#sec-canonicalizeunicodelocaleid
 * IMPORTANT: This modifies the object inline
 * @param locale
 */
export function canonicalizeUnicodeLocaleId (locale) {
  locale.lang = canonicalizeUnicodeLanguageId(locale.lang)
  if (locale.extensions) {
    for (const extension of locale.extensions) {
      switch (extension.type) {
        case 'u':
          extension.keywords = canonicalizeKVs(extension.keywords)
          if (extension.attributes) {
            extension.attributes = canonicalizeAttrs(extension.attributes)
          }
          break
        case 't':
          if (extension.lang) {
            extension.lang = canonicalizeUnicodeLanguageId(extension.lang)
          }
          extension.fields = canonicalizeKVs(extension.fields)
          break
        default:
          extension.value = extension.value.toLowerCase()
          break
      }
    }
    locale.extensions.sort(compareExtension)
  }

  return locale
}

export function emitUnicodeLanguageId (lang) {
  if (!lang) {
    return ''
  }
  return [lang.lang, lang.script, lang.region, ...(lang.variants || [])]
    .filter(Boolean)
    .join('-')
}

export function emitUnicodeLocaleId ({ lang, extensions }) {
  const chunks = [emitUnicodeLanguageId(lang)]
  for (const ext of extensions) {
    chunks.push(ext.type)
    switch (ext.type) {
      case 'u':
        chunks.push(
          ...ext.attributes,
          ...ext.keywords.reduce((all, kv) => all.concat(kv), [])
        )
        break
      case 't':
        chunks.push(
          emitUnicodeLanguageId(ext.lang),
          ...ext.fields.reduce((all, kv) => all.concat(kv), [])
        )
        break
      default:
        chunks.push(ext.value)
        break
    }
  }

  return chunks.filter(Boolean).join('-')
}

/**
 * https://tc39.es/ecma402/#sec-canonicalizelocalelist
 * @param {string | Array<string>} locales
 */
export function getCanonicalLocales (locales) {
  if (locales === undefined) {
    return []
  }
  const seen = []

  if (!Array.isArray(locales)) {
    locales = String(locales)
  }

  if (typeof locales === 'string') {
    locales = [locales]
  }

  for (const locale of locales) {
    const canonicalizedTag = emitUnicodeLocaleId(
      canonicalizeUnicodeLocaleId(parseUnicodeLocaleId(String(locale)))
    )
    if (seen.indexOf(canonicalizedTag) < 0) {
      seen.push(canonicalizedTag)
    }
  }
  return seen
}

export function getCalendarPreferenceDataForRegion (region) {
  const _region = region ? region.toUpperCase() : null

  return (calendars[_region || ''] || calendars['001']).map(c => {
    // Resolve aliases
    // cldr-json/cldr-code v42.0.0-ALPHA3-2
    // https://github.com/unicode-org/cldr-json/blob/42.0.0-ALPHA3-2/cldr-json/cldr-bcp47/bcp47/calendar.json
    if (c === 'gregorian') {
      return 'gregory'
    }

    if (c === 'islamic-civil') {
      return 'islamicc'
    }

    // At the time this has been written this calendar was not listed in
    // the supplemental calendarPreferenceData file
    // https://github.com/unicode-org/cldr-json/blob/42.0.0-ALPHA3-2/cldr-json/cldr-core/supplemental/calendarPreferenceData.json
    // if (c === 'ethiopic-amete-alem') {
    //   return 'ethioaa'
    // }

    return c
  })
}

export function getHourCyclesPreferenceDataForLocaleOrRegion (locale, region) {
  const _locale = locale.toLowerCase()
  const _region = region ? region.toUpperCase() : ''

  const hourCyclesPreference =
    hourCycles[_locale] ||
    hourCycles[_region] ||
    hourCycles[`${_locale}-001`] ||
    hourCycles['001']

  return [...hourCyclesPreference]
}

export function getTimeZonePreferenceForRegion (region) {
  const territory = region.toLowerCase()

  if (timeZones[territory]) {
    return [...timeZones[territory]].sort()
  }

  return []
}

export function getWeekDataForRegion (region) {
  const _region = region ? region.toUpperCase() : ''

  return weekData[_region || '001'] || weekData['001']
}

export function invariant (condition, message, Err = Error) {
  if (!condition) {
    throw new Err(message)
  }
}

export function applyOptionsToTag (tag, options) {
  // invariant(typeof tag === 'string', 'language tag must be a string')
  invariant(
    isStructurallyValidLanguageTag(tag),
    'malformed language tag',
    RangeError
  )

  const language = getOption(
    options,
    'language',
    'string',
    undefined,
    undefined
  )

  if (language !== undefined) {
    invariant(
      isUnicodeLanguageSubtag(language),
      'Malformed unicode_language_subtag',
      RangeError
    )
  }

  const script = getOption(options, 'script', 'string', undefined, undefined)

  if (script !== undefined) {
    invariant(
      isUnicodeScriptSubtag(script),
      'Malformed unicode_script_subtag',
      RangeError
    )
  }

  const region = getOption(options, 'region', 'string', undefined, undefined)

  if (region !== undefined) {
    invariant(
      isUnicodeRegionSubtag(region),
      'Malformed unicode_region_subtag',
      RangeError
    )
  }

  const languageId = parseUnicodeLanguageId(tag)
  if (language !== undefined) {
    languageId.lang = language
  }
  if (script !== undefined) {
    languageId.script = script
  }
  if (region !== undefined) {
    languageId.region = region
  }

  return Intl.getCanonicalLocales(
    emitUnicodeLocaleId({
      ...parseUnicodeLocaleId(tag),
      lang: languageId
    })
  )[0]
}

export function applyUnicodeExtensionToTag (tag, options, relevantExtensionKeys) {
  let unicodeExtension
  let keywords = []
  const ast = parseUnicodeLocaleId(tag)

  for (const ext of ast.extensions) {
    if (ext.type === 'u') {
      unicodeExtension = ext
      if (Array.isArray(ext.keywords)) keywords = ext.keywords
    }
  }

  const result = Object.create(null)

  for (const key of relevantExtensionKeys) {
    let value, entry
    for (const keyword of keywords) {
      if (keyword[0] === key) {
        entry = keyword
        value = entry[1]
      }
    }
    invariant(key in options, `${key} must be in options`)
    const optionsValue = options[key]
    if (optionsValue !== undefined) {
      invariant(
        typeof optionsValue === 'string',
        `Value for ${key} must be a string`
      )
      value = optionsValue
      if (entry) {
        entry[1] = value
      } else {
        keywords.push([key, value])
      }
    }
    result[key] = value
  }
  if (!unicodeExtension) {
    if (keywords.length) {
      ast.extensions.push({
        type: 'u',
        keywords,
        attributes: []
      })
    }
  } else {
    unicodeExtension.keywords = keywords
  }
  result.baseName = Intl.getCanonicalLocales(emitUnicodeLanguageId(ast.lang))[0]
  return result
}

export function mergeUnicodeLanguageId (
  lang,
  script,
  region,
  variants = [],
  replacement
) {
  if (!replacement) {
    return {
      lang: lang || 'und',
      script,
      region,
      variants
    }
  }
  return {
    lang: !lang || lang === 'und' ? replacement.lang : lang,
    script: script || replacement.script,
    region: region || replacement.region,
    variants: [...variants, ...replacement.variants]
  }
}

export function addLikelySubtags (tag) {
  const ast = parseUnicodeLocaleId(tag)
  const unicodeLangId = ast.lang
  const { lang, script, region, variants } = unicodeLangId
  if (script && region) {
    const match =
      likelySubtags[
        emitUnicodeLanguageId({ lang, script, region, variants: [] })
      ]
    if (match) {
      const parts = parseUnicodeLanguageId(match)
      ast.lang = mergeUnicodeLanguageId(
        undefined,
        undefined,
        undefined,
        variants,
        parts
      )
      return emitUnicodeLocaleId(ast)
    }
  }
  if (script) {
    const match =
      likelySubtags[emitUnicodeLanguageId({ lang, script, variants: [] })]
    if (match) {
      const parts = parseUnicodeLanguageId(match)
      ast.lang = mergeUnicodeLanguageId(
        undefined,
        undefined,
        region,
        variants,
        parts
      )
      return emitUnicodeLocaleId(ast)
    }
  }
  if (region) {
    const match =
      likelySubtags[emitUnicodeLanguageId({ lang, region, variants: [] })]
    if (match) {
      const parts = parseUnicodeLanguageId(match)
      ast.lang = mergeUnicodeLanguageId(
        undefined,
        script,
        undefined,
        variants,
        parts
      )
      return emitUnicodeLocaleId(ast)
    }
  }
  const match =
    likelySubtags[lang] ||
    likelySubtags[emitUnicodeLanguageId({ lang: 'und', script, variants: [] })]
  if (!match) {
    throw new Error(`No match for addLikelySubtags`)
  }
  const parts = parseUnicodeLanguageId(match)
  ast.lang = mergeUnicodeLanguageId(undefined, script, region, variants, parts)
  return emitUnicodeLocaleId(ast)
}

/**
 * From: https://github.com/unicode-org/icu/blob/4231ca5be053a22a1be24eb891817458c97db709/icu4j/main/classes/core/src/com/ibm/icu/util/ULocale.java#L2395
 * @param tag
 */
export function removeLikelySubtags (tag) {
  let maxLocale = addLikelySubtags(tag)
  if (!maxLocale) {
    return tag
  }
  maxLocale = emitUnicodeLanguageId({
    ...parseUnicodeLanguageId(maxLocale),
    variants: []
  })
  const ast = parseUnicodeLocaleId(tag)
  const {
    lang: { lang, script, region, variants }
  } = ast
  const trial = addLikelySubtags(emitUnicodeLanguageId({ lang, variants: [] }))
  if (trial === maxLocale) {
    return emitUnicodeLocaleId({
      ...ast,
      lang: mergeUnicodeLanguageId(lang, undefined, undefined, variants)
    })
  }
  if (region) {
    const trial = addLikelySubtags(
      emitUnicodeLanguageId({ lang, region, variants: [] })
    )
    if (trial === maxLocale) {
      return emitUnicodeLocaleId({
        ...ast,
        lang: mergeUnicodeLanguageId(lang, undefined, region, variants)
      })
    }
  }
  if (script) {
    const trial = addLikelySubtags(
      emitUnicodeLanguageId({ lang, script, variants: [] })
    )
    if (trial === maxLocale) {
      return emitUnicodeLocaleId({
        ...ast,
        lang: mergeUnicodeLanguageId(lang, script, undefined, variants)
      })
    }
  }
  return tag
}

export function createArrayFromListOrRestricted (list, restricted) {
  let result = list
  if (restricted !== undefined) {
    result = [restricted]
  }

  return Array.from(result)
}

export function translateCharacterOrder (order) {
  if (order === 'right-to-left') {
    return 'rtl'
  }

  return 'ltr'
}

export function getOption (opts, prop, type, values, fallback) {
  if (typeof opts !== 'object') {
    throw new TypeError('Options must be an object')
  }
  let value = opts[prop]
  if (value !== undefined) {
    if (type !== 'boolean' && type !== 'string') {
      throw new TypeError('invalid type')
    }
    if (type === 'boolean') {
      value = Boolean(value)
    }
    if (type === 'string') {
      value = String(value)
    }
    if (values !== undefined && !values.filter(val => val === value).length) {
      throw new RangeError(`${value} is not within ${values.join(', ')}`)
    }
    return value
  }
  return fallback
}

export function supportedValuesOf (key, locale) {
  switch (key) {
    case 'calendar':
      return getSupportedCalendars(locale)
    case 'collation':
      return getSupportedCollations(locale)
    case 'currency':
      return getSupportedCurrencies(locale)
    case 'numberingSystem':
      return getSupportedNumberingSystems(locale)
    case 'timeZone':
      return getSupportedTimeZones(locale)
    case 'unit':
      return getSupportedUnits(locale)
    default:
      throw RangeError('Invalid key: ' + key)
  }
}

function isSupportedCalendar (item, locale = 'en') {
  try {
    const dateTimeFormat = new Intl.DateTimeFormat(`${locale}-u-ca-${item}`)
    const options = dateTimeFormat.resolvedOptions().calendar

    if (item !== 'gregory' || options !== 'gregory') return true
  } catch (_err) {}

  return false
}

export function getSupportedCalendars (localePrefix) {
  return calendarNames.filter(calendar =>
    isSupportedCalendar(calendar, localePrefix)
  )
}

function isSupportedCol (collation, locale = 'en') {
  try {
    return (
      Intl.Collator(`${locale}-u-co-${collation}`).resolvedOptions()
        .collation === collation
    )
  } catch (_err) {}

  return false
}

export function getSupportedCollations (locale) {
  return collations.filter(collation => isSupportedCol(collation, locale))
}

function isSupportedNumberingSystem (system, locale = 'en') {
  try {
    const numberFormat = new Intl.NumberFormat(`${locale}-u-nu-${system}`)
    const options = numberFormat.resolvedOptions().numberingSystem

    if (
      (options === system && system === 'latn') ||
      numberFormat.format(123) !== '123'
    ) {
      return true
    }
  } catch (_err) {}

  return false
}

export function getSupportedNumberingSystems (locale) {
  return numberingSystemNames.filter(numberingSystemName =>
    isSupportedNumberingSystem(numberingSystemName, locale)
  )
}

function isSupportedTZ (timeZone, locale = 'en') {
  try {
    const formatter = new Intl.DateTimeFormat(locale, { timeZone })
    return formatter.resolvedOptions().timeZone === timeZone
  } catch (_err) {}

  return false
}

export function getCanonicalTimeZoneIdentifier (timeZoneIdentifier) {
  timeZoneIdentifier = timeZoneIdentifier.toUpperCase()

  for (const [ianaTZ, cldcTZ, link] of timeZoneNames) {
    if (ianaTZ.toUpperCase() === timeZoneIdentifier) {
      return ianaTZ
    }
    if (cldcTZ && cldcTZ.toUpperCase() === timeZoneIdentifier) {
      return ianaTZ
    }
    if (link && link.toUpperCase() === timeZoneIdentifier) {
      return ianaTZ
    }
  }
}

export function getSupportedTimeZones (locale) {
  const supportedTimeZoneNames = []

  for (const [ianaTZ, cldcTZ, link] of timeZoneNames) {
    if (isSupportedTZ(ianaTZ, locale)) {
      supportedTimeZoneNames.push(ianaTZ)
    }
    if (cldcTZ && isSupportedTZ(cldcTZ, locale)) {
      supportedTimeZoneNames.push(cldcTZ)
    }
    if (link && isSupportedTZ(link, locale)) {
      supportedTimeZoneNames.push(link)
    }
  }

  return supportedTimeZoneNames
}

function isSupportedU (unit, locale = 'en') {
  try {
    const formatter = new Intl.NumberFormat(locale, { style: 'unit', unit })
    return formatter.resolvedOptions().unit === unit
  } catch (_err) {}

  return false
}

export function getSupportedUnits (locale) {
  return units.filter(unit => isSupportedU(unit, locale))
}
export function isSupportedCurrency (currency, locale = 'en') {
  try {
    const numberFormat = new Intl.NumberFormat(locale, {
      style: 'currency',
      currencyDisplay: 'name',
      currency
    })

    const format = numberFormat.format(123)

    if (
      format.substring(0, 3) !== currency &&
      format.substring(format.length - 3) !== currency
    ) {
      return true
    }
  } catch (_err) {}

  return false
}

export function getSupportedCurrencies (locale) {
  const ATOZ = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const supportedCurrencies = []

  for (const currency of currencies) {
    if (currency.length === 3) {
      if (isSupportedCurrency(currency, locale)) {
        supportedCurrencies.push(currency)
      }
    } else if (currency.length === 5 && currency[3] === '~') {
      const start = ATOZ.indexOf(currency[2])
      const end = ATOZ.indexOf(currency[4])

      for (let i = start; i <= end; i++) {
        const currentCurrency = currency.substring(0, 2) + ATOZ[i]
        if (isSupportedCurrency(currentCurrency, locale)) {
          supportedCurrencies.push(currentCurrency)
        }
      }
    }
  }

  return supportedCurrencies
}

// NumberFormat

export function defaultNumberOption(
  val,
  min,
  max,
  fallback
) {
  if (val !== undefined) {
    val = Number(val)
    if (isNaN(val) || val < min || val > max) {
      throw new RangeError(`${val} is outside of range [${min}, ${max}]`)
    }
    return Math.floor(val)
  }
  return fallback
}

export function getNumberOption (
  options,
  property,
  minimum,
  maximum,
  fallback
) {
  const val = options[property]
  // @ts-expect-error
  return defaultNumberOption(val, minimum, maximum, fallback)
}

/**
 * https://tc39.es/ecma402/#sec-setnfdigitoptions
 */
export function setNumberFormatDigitOptions(
  internalSlots,
  opts,
  mnfdDefault,
  mxfdDefault,
  notation
) {
  const mnid = getNumberOption(opts, 'minimumIntegerDigits', 1, 21, 1)
  let mnfd = opts.minimumFractionDigits
  let mxfd = opts.maximumFractionDigits
  let mnsd = opts.minimumSignificantDigits
  let mxsd = opts.maximumSignificantDigits
  internalSlots.minimumIntegerDigits = mnid
  const roundingPriority = getOption(
    opts,
    'roundingPriority',
    'string',
    ['auto', 'morePrecision', 'lessPrecision'],
    'auto'
  )
  const hasSd = mnsd !== undefined || mxsd !== undefined

  const hasFd = mnfd !== undefined || mxfd !== undefined

  let needSd = true
  let needFd = true
  if (roundingPriority === 'auto') {
    needSd = hasSd
    if (hasSd || (!hasFd && notation === 'compact')) {
      needFd = false
    }
  }
  if (needSd) {
    if (hasSd) {
      mnsd = defaultNumberOption(mnsd, 1, 21, 1)
      mxsd = defaultNumberOption(mxsd, mnsd, 21, 21)
      internalSlots.minimumSignificantDigits = mnsd
      internalSlots.maximumSignificantDigits = mxsd
    } else {
      internalSlots.minimumSignificantDigits = 1
      internalSlots.maximumSignificantDigits = 21
    }
  }
  if (needFd) {
    if (hasFd) {
      // @ts-expect-error
      mnfd = defaultNumberOption(mnfd, 0, 20, undefined)
      // @ts-expect-error
      mxfd = defaultNumberOption(mxfd, 0, 20, undefined)
      if (mnfd === undefined) {
        mnfd = Math.min(mnfdDefault, mxfd)
      } else if (mxfd === undefined) {
        mxfd = Math.max(mxfdDefault, mnfd)
      } else if (mnfd > mxfd) {
        throw new RangeError(`Invalid range, ${mnfd} > ${mxfd}`)
      }
      internalSlots.minimumFractionDigits = mnfd
      internalSlots.maximumFractionDigits = mxfd
    } else {
      internalSlots.minimumFractionDigits = mnfdDefault
      internalSlots.maximumFractionDigits = mxfdDefault
    }
  }
  if (needSd || needFd) {
    if (roundingPriority === 'morePrecision') {
      internalSlots.roundingType = 'morePrecision'
    } else if (roundingPriority === 'lessPrecision') {
      internalSlots.roundingType = 'lessPrecision'
    } else if (hasSd) {
      internalSlots.roundingType = 'significantDigits'
    } else {
      internalSlots.roundingType = 'fractionDigits'
    }
  } else {
    internalSlots.roundingType = 'morePrecision'
    internalSlots.minimumFractionDigits = 0
    internalSlots.maximumFractionDigits = 0
    internalSlots.minimumSignificantDigits = 1
    internalSlots.maximumSignificantDigits = 2
  }
}

/**
 * http://ecma-international.org/ecma-402/7.0/index.html#sec-getoperands
 * @param s
 */
export function getOperands(s) {
  invariant(
    typeof s === 'string',
    `getOperands should have been called with a string`
  )
  const n = Number(s)
  invariant(isFinite(n), 'n should be finite')
  let dp = s.indexOf('.')
  let iv
  let f
  let v
  let fv = ''
  if (dp === -1) {
    iv = n
    f = 0
    v = 0
  } else {
    iv = s.slice(0, dp)
    fv = s.slice(dp, s.length)
    f = Number(fv)
    v = fv.length
  }
  const i = Math.abs(Number(iv))
  let w
  let t
  if (f !== 0) {
    const ft = fv.replace(/0+$/, '')
    w = ft.length
    t = Number(ft)
  } else {
    w = 0
    t = 0
  }
  return {
    Number: n,
    IntegerDigits: i,
    NumberOfFractionDigits: v,
    NumberOfFractionDigitsWithoutTrailing: w,
    FractionDigits: f,
    FractionDigitsWithoutTrailing: t,
  }
}

// LookUp Matcher
// Resolve
/** @typedef {{ locale: string, dataLocale: string, [k: string]: any }} ResolveLocaleResult */

/**
 * https://tc39.es/ecma402/#sec-resolvelocale
 * @param {Set<string>} availableLocales
 * @param {string[]} requestedLocales
 * @param {{localeMatcher: string; [k: string]: string}} options
 * @param {K[]} relevantExtensionKeys
 * @param {Record<string, D | undefined>} localeData
 * @param {() => string} getDefaultLocale
 * @return {ResolveLocaleResult}
 */
export function resolveLocale (
  availableLocales,
  requestedLocales,
  options,
  relevantExtensionKeys,
  localeData,
  getDefaultLocale
) {
  const matcher = options.localeMatcher
  let r
  if (matcher === 'lookup') {
    r = lookupMatcher(availableLocales, requestedLocales, getDefaultLocale)
  } else {
    r = bestFitMatcher(availableLocales, requestedLocales, getDefaultLocale)
  }
  let foundLocale = r.locale
  const result = { locale: '', dataLocale: foundLocale }
  let supportedExtension = '-u'
  for (const key of relevantExtensionKeys) {
    invariant(
      foundLocale in localeData,
      `Missing locale data for ${foundLocale}`
    )
    const foundLocaleData = localeData[foundLocale]
    invariant(
      typeof foundLocaleData === 'object' && foundLocaleData !== null,
      `locale data ${key} must be an object`
    )
    const keyLocaleData = foundLocaleData[key]
    invariant(
      Array.isArray(keyLocaleData),
      `keyLocaleData for ${key} must be an array`
    )
    let value = keyLocaleData[0]
    invariant(
      typeof value === 'string' || value === null,
      `value must be string or null but got ${typeof value} in key ${key}`
    )
    let supportedExtensionAddition = ''
    if (r.extension) {
      const requestedValue = unicodeExtensionValue(r.extension, key)
      if (requestedValue !== undefined) {
        if (requestedValue !== '') {
          if (~keyLocaleData.indexOf(requestedValue)) {
            value = requestedValue
            supportedExtensionAddition = `-${key}-${value}`
          }
        } else if (~requestedValue.indexOf('true')) {
          value = 'true'
          supportedExtensionAddition = `-${key}`
        }
      }
    }
    if (key in options) {
      const optionsValue = options[key]
      invariant(
        typeof optionsValue === 'string' ||
          typeof optionsValue === 'undefined' ||
          optionsValue === null,
        'optionsValue must be String, Undefined or Null'
      )
      if (~keyLocaleData.indexOf(optionsValue)) {
        if (optionsValue !== value) {
          value = optionsValue
          supportedExtensionAddition = ''
        }
      }
    }
    result[key] = value
    supportedExtension += supportedExtensionAddition
  }
  if (supportedExtension.length > 2) {
    const privateIndex = foundLocale.indexOf('-x-')
    if (privateIndex === -1) {
      foundLocale = foundLocale + supportedExtension
    } else {
      const preExtension = foundLocale.slice(0, privateIndex)
      const postExtension = foundLocale.slice(privateIndex, foundLocale.length)
      foundLocale = preExtension + supportedExtension + postExtension
    }
    foundLocale = getCanonicalLocales(foundLocale)[0]
  }
  result.locale = foundLocale
  return result
}

/**
 * https://tc39.es/ecma402/#sec-unicodeextensionvalue
 * @param extension
 * @param key
 */
export function unicodeExtensionValue(extension, key) {
  invariant(key.length === 2, 'key must have 2 elements')
  const size = extension.length
  let searchValue = `-${key}-`
  let pos = extension.indexOf(searchValue)
  if (pos !== -1) {
    const start = pos + 4
    let end = start
    let k = start
    let done = false
    while (!done) {
      const e = extension.indexOf('-', k)
      let len
      if (e === -1) {
        len = size - k
      } else {
        len = e - k
      }
      if (len === 2) {
        done = true
      } else if (e === -1) {
        end = size
        done = true
      } else {
        end = e
        k = e + 1
      }
    }
    return extension.slice(start, end)
  }
  searchValue = `-${key}`
  pos = extension.indexOf(searchValue)
  if (pos !== -1 && pos + 3 === size) {
    return ''
  }
  return undefined
}

/**
 * https://tc39.es/ecma402/#sec-lookupmatcher
 * @param {Set<string>} availableLocales
 * @param {string[]} requestedLocales
 * @param {() => string} getDefaultLocale
 */
export function lookupMatcher(
  availableLocales,
  requestedLocales,
  getDefaultLocale
) {
  const result = {locale: ''}
  for (const locale of requestedLocales) {
    const noExtensionLocale = locale.replace(
      UNICODE_EXTENSION_SEQUENCE_REGEX,
      ''
    )
    const availableLocale = bestAvailableLocale(
      availableLocales,
      noExtensionLocale
    )
    if (availableLocale) {
      result.locale = availableLocale
      if (locale !== noExtensionLocale) {
        result.extension = locale.slice(
          noExtensionLocale.length + 1,
          locale.length
        )
      }
      return result
    }
  }
  result.locale = getDefaultLocale()
  return result
}

/**
 * https://tc39.es/ecma402/#sec-bestfitmatcher
 * @param {Set<string>} availableLocales
 * @param {string[]} requestedLocales
 * @param {() => string} getDefaultLocale
 */
export function bestFitMatcher(
  availableLocales,
  requestedLocales,
  getDefaultLocale
) {
  const minimizedAvailableLocaleMap = {}
  const availableLocaleMap = {}
  const canonicalizedLocaleMap = {}
  const minimizedAvailableLocales = new Set()
  availableLocales.forEach(locale => {
    const minimizedLocale = new Locale(locale)
      .minimize()
      .toString()

    const canonicalizedLocale = getCanonicalLocales(locale)[0] || locale

    minimizedAvailableLocaleMap[minimizedLocale] = locale
    availableLocaleMap[locale] = locale
    canonicalizedLocaleMap[canonicalizedLocale] = locale
    minimizedAvailableLocales.add(minimizedLocale)
    minimizedAvailableLocales.add(locale)
    minimizedAvailableLocales.add(canonicalizedLocale)
  })

  let foundLocale
  for (const l of requestedLocales) {
    if (foundLocale) {
      break
    }
    const noExtensionLocale = l.replace(UNICODE_EXTENSION_SEQUENCE_REGEX, '')

    if (availableLocales.has(noExtensionLocale)) {
      foundLocale = noExtensionLocale
      break
    }

    if (minimizedAvailableLocales.has(noExtensionLocale)) {
      foundLocale = noExtensionLocale
      break
    }

    const locale = new Locale(noExtensionLocale)

    const maximizedRequestedLocale = locale.maximize().toString()
    const minimizedRequestedLocale = locale.minimize().toString()

    // Check minimized locale
    if (minimizedAvailableLocales.has(minimizedRequestedLocale)) {
      foundLocale = minimizedRequestedLocale
      break
    }

    // Lookup algo on maximized locale
    foundLocale = bestAvailableLocale(
      minimizedAvailableLocales,
      maximizedRequestedLocale
    )
  }
  if (!foundLocale) {
    return {locale: getDefaultLocale()}
  }
  return {
    locale:
      availableLocaleMap[foundLocale] ||
      canonicalizedLocaleMap[foundLocale] ||
      minimizedAvailableLocaleMap[foundLocale] ||
      foundLocale,
  }
}

/**
 * https://tc39.es/ecma402/#sec-bestavailablelocale
 * @param availableLocales
 * @param locale
 */
export function bestAvailableLocale(
  availableLocales,
  locale
) {
  let candidate = locale
  while (true) {
    if (availableLocales.has(candidate)) {
      return candidate
    }
    let pos = candidate.lastIndexOf('-')
    if (!~pos) {
      return undefined
    }
    if (pos >= 2 && candidate[pos - 2] === '-') {
      pos -= 2
    }
    candidate = candidate.slice(0, pos)
  }
}


/**
 * https://tc39.es/ecma402/#sec-lookupsupportedlocales
 * @param {Set<string>} availableLocales
 * @param {Array<string>} requestedLocales
 */
export function lookupSupportedLocales (
  availableLocales,
  requestedLocales
) {
  const subset = []
  for (const locale of requestedLocales) {
    const noExtensionLocale = locale.replace(
      UNICODE_EXTENSION_SEQUENCE_REGEX,
      ''
    )
    const availableLocale = bestAvailableLocale(
      availableLocales,
      noExtensionLocale
    )
    if (availableLocale) {
      subset.push(availableLocale)
    }
  }
  return subset
}

// Supported Locales

/**
 * https://tc39.es/ecma402/#sec-supportedlocales
 * @param {Set<string>} availableLocales
 * @param requestedLocales
 * @param options
 */
export function SupportedLocales(
  availableLocales,
  requestedLocales,
  options
) {
  let matcher = 'best fit'
  if (options !== undefined) {
    options = Object(options)
    matcher = getOption(
      options,
      'localeMatcher',
      'string',
      ['lookup', 'best fit'],
      'best fit'
    )
  }
  if (matcher === 'best fit') {
    return lookupSupportedLocales(availableLocales, requestedLocales)
  }
  return lookupSupportedLocales(availableLocales, requestedLocales)
}

export function toString (value) {
  if (typeof value === 'symbol') {
    throw new TypeError('Cannot convert a Symbol value to a String')
  }

  return String(value)
}

export function toIntegerThrowOnInfinity (value) {
  const integer = Integer(value)
  if (!Number.isFinite(integer)) {
    throw new RangeError('infinity is out of range')
  }
  return integer
}

export function toPositiveInteger (valueParam, property) {
  const value = Integer(valueParam)
  if (!Number.isFinite(value)) {
    throw new RangeError('infinity is out of range')
  }
  if (value < 1) {
    if (property !== undefined) {
      throw new RangeError(
        `property '${property}' cannot be a a number less than one`
      )
    }
    throw new RangeError(
      'Cannot convert a number less than one to a positive integer'
    )
  }
  return value
}

export function toIntegerWithoutRounding (valueParam) {
  const value = Number(valueParam)
  if (Number.isNaN(value)) return 0
  if (!Number.isFinite(value)) {
    throw new RangeError('infinity is out of range')
  }

  if (!isInteger(value)) {
    throw new RangeError(`unsupported fractional value ${value}`)
  }

  return Integer(value) // ℝ(value) in spec text; converts -0 to 0
}

export function getType (input) {
  if (input === null) {
    return 'Null'
  }

  switch (typeof input) {
    case 'undefined':
      return 'Undefined'
    case 'function':
    case 'object':
      return 'Object'
    case 'number':
      return 'Number'
    case 'boolean':
      return 'Boolean'
    case 'string':
      return 'String'
    case 'symbol':
      return 'Symbol'
    case 'bigint':
      return 'Bigint'
  }
}

// use WeakMap to store the object->key mapping
// so the objects can be garbage collected.
// WeakMap uses a hashtable under the hood, so the lookup
// complexity is almost O(1).
const table = new WeakMap()

// counter of the key
let counter = 0

// A stable hashObject implementation that supports:
// - Fast and ensures unique hashObject properties
// - Handles unserializable values
// - Handles object key ordering
// - Generates short results
//
// This is not a serialization function, and the result is not guaranteed to be
// parsable.
export function hashObject (arg) {
  const type = typeof arg
  const constructor = arg?.constructor
  const isDate = constructor === Date

  let result = ''
  let index

  if (isDate) {
    result = `${arg.toJSON()}`
  } else if (Object(arg) === arg && constructor !== RegExp) {
    // Object/function, not null/date/regexp. Use WeakMap to store the id first.
    // If it's already hashed, directly return the result.
    const resultFromMap = table.get(arg)
    if (resultFromMap) return resultFromMap

    // Store the hashObject first for circular reference detection before entering the
    // recursive `stableHash` calls.
    // For other objects like set and map, we use this id directly as the hashObject.
    result = `-${++counter}-`
    table.set(arg, result)

    if (constructor === Array) {
      // Array.
      result = '['
      index = -1

      while (++index < arg.length) {
        result += `${hashObject(arg[index])},`
      }

      table.set(arg, result)
    }
    if (constructor === Object) {
      // Object, sort keys.
      result = '{'
      const keys = Object.keys(arg).sort()

      while (!isUndefined((index = keys.pop()))) {
        if (!isUndefined(arg[index])) {
          result += `${index}:${hashObject(arg[index])},`
        }
      }

      table.set(arg, result)
    }
  } else {
    result = type === 'symbol'
      ? arg.toString()
      : type === 'string'
        ? JSON.stringify(arg)
        : String(arg)
  }

  return result
}

class LRUCacheEntry {
  constructor (key, value) {
    this.key = key
    this.value = value
    this.createdAt = Date.now()
    this.modifiedAt = this.createdAt
  }
}

class Interval {
  /** @type {null | NodeJS.Timer | number} */
  #id = null
  #isSet = false
  #isRunning = false
  #ms
  #callback

  /**
   *
   * @param {() => void} callback
   * @param {number | undefined} ms
   */
  constructor (callback, ms) {
    this.#ms = ms
    this.#callback = callback
  }

  get id () {
    return this.#id
  }

  get isSet () {
    return this.#isSet
  }

  get isRunning () {
    return this.#isRunning
  }

  /**
   *
   * @param {() => void} callback
   */
  setCallback (callback) {
    this.#callback = callback
    return this
  }

  set () {
    if (this.#isSet) {
      this.clear()
    }

    this.#id = setInterval(() => {
      this.#isRunning = true
      this.#callback()
      this.#isRunning = false
    }, this.#ms)
    this.#isSet = true
    return this
  }

  clear () {
    if (this.#id !== null || this.#isSet) {
      clearInterval(this.#id)
    }

    this.#isSet = false
    this.#isRunning = false
    this.#id = null
    return this
  }
}

export class LRUCache extends Map {
  #maxSize
  #maxAge
  #interval
  /** @type {Map<string, Set<(entry: LRUCacheEntry | Error) => void>>} */
  #listeners
  #notFoundReturnValue
  #allowStale
  /** @type {(value: any, key: any) => number} */
  #sizeCalculation
  #useInterval
  #size = 0

  constructor (
    {
      maxSize = 1e4, // 10,000
      maxAge = Infinity,
      intervalMilliseconds = 1000 * 60,
      allowStale = false,
      useInterval = false,
      sizeCalculation,
      notFoundReturnValue
    } = {
      maxSize: 1e4,
      maxAge: Infinity,
      intervalMilliseconds: 1000 * 60,
      allowStale: false,
      useInterval: false
    }
  ) {
    super()
    this.#maxSize = maxSize // How many items to store in cache at max.
    this.#maxAge = maxAge
    this.#notFoundReturnValue = notFoundReturnValue
    this.#allowStale = allowStale
    this.#useInterval = typeof useInterval !== 'boolean' ? false : useInterval

    if (typeof sizeCalculation !== 'function') {
      this.#sizeCalculation = (value, key) => {
        return 1
      }
    } else {
      this.#sizeCalculation = sizeCalculation
    }

    this.#listeners = new Map([
      ['expired', new Set()],
      ['evicted', new Set()],
      ['error', new Set()]
    ])

    this.#interval = new Interval(() => {
      this.#checkMaxAge()
    }, intervalMilliseconds)
  }

  get maxSize () {
    return this.#maxSize
  }

  get maxAge () {
    return this.#maxAge
  }

  get size () {
    return this.#size
  }

  clearInterval () {
    this.#interval.clear()
  }

  #checkMaxAge () {
    const expiredTimestamp = Date.now() - this.#maxAge

    for (const entry of super.values()) {
      if (expiredTimestamp > entry.modifiedAt) {
        this.delete(entry.key)
        this.#emit('expired', entry)
      }
    }
  }

  #emit (eventName, ...args) {
    const eventSet = this.#listeners.get(eventName)

    if (
      typeof eventSet === 'undefined' ||
      eventSet.size === 0
    ) {
      return this
    }

    for (const eventListener of eventSet.values()) {
      const potentialPromise = eventListener(...args)
      if (
        typeof potentialPromise === 'object' &&
        typeof potentialPromise.then === 'function'
      ) {
        if (eventName !== 'error') {
          potentialPromise
            .catch(err => {
              this.emit('error', err)
            })
        }
      }
    }

    return this
  }

  /**
   *
   * @param {'error' | 'expired' | 'evicted'} eventName
   * @param {(entry: LRUCacheEntry | Error) => void} callback
   */
  addListener (eventName, callback) {
    const eventSet = this.#listeners.get(eventName)

    if (typeof eventSet === 'undefined') {
      return this
    }

    if (!eventSet.has(callback)) {
      eventSet.add(callback)
      this.#listeners.set(eventName, eventSet)
    }

    return this
  }

  /**
   *
   * @param {string} eventName
   * @param {(entry: LRUCacheEntry | Error) => void} callback
   */
  removeListener (eventName, callback) {
    const eventSet = this.#listeners.get(eventName)

    if (typeof eventSet === 'undefined' || eventSet.size === 0) {
      return this
    }

    if (eventSet.has(callback)) {
      eventSet.delete(callback)
      this.#listeners.set(eventName, eventSet)
    }

    return this
  }

  clear () {
    super.clear()
    this.#interval.clear()
  }

  delete (key) {
    const entry = super.get(key)
    const result = super.delete(key)

    if (entry !== undefined) {
      const size = this.#sizeCalculation(entry.value, key)

      if (typeof size !== 'number') {
        throw new TypeError('sizeCalculation should return number to be added to size of cache.')
      }

      this.#size -= size
      this.#emit('evicted', entry.value)
    }

    if (this.#useInterval && this.size === 0) {
      this.#interval.clear()
    }

    return result
  }

  /**
   * Returns an iterable object yielding the entries in the cache.
   * In order from most recently used to least recently used.
   * @returns {IterableIterator<[any, any]>}
   */
  entries () {
    const entriesMap = [...super.entries()]
    let index = entriesMap.length

    const res = {
      * [Symbol.iterator] () {
        while (--index >= 0) {
          yield [entriesMap[index][0], entriesMap[index][1].value]
        }
      }
    }

    return res
  }

  * [Symbol.iterator] () {
    return this.entries()
  }

  /**
   * Returns the cached value by searching the cache.
   * @param {(value: any, key: any, cache: LRUCache) => any} findFunction Should return truthy value when item found.
   * @param {{ allowStale: boolean } | undefined} options
   * @returns {any}
   */
  find (findFunction, options = {}) {
    const entriesMap = [...super.entries()]
    let index = entriesMap.length

    while (--index >= 0) {
      const indexRes = findFunction(entriesMap[index][1].value, entriesMap[index][0], this)
      if (indexRes) {
        return this.get(entriesMap[index][0], options)
      }
    }
  }

  /**
   *
   * @param {(value: any, key: any) => void} callback
   */
  forEach (callback) {
    super.forEach((entry, key) => {
      callback(entry.value, key)
    })
  }

  /**
   * Returns the cached value by its key.
   * Time complexity: O(1) in average.
   * @param {string} key
   * @param {{ allowStale: boolean } | undefined} options
   * @returns {any}
   */
  get (key, options = {}) {
    const entry = super.get(key)

    if (entry === undefined) {
      return this.#notFoundReturnValue
    }
    const allowStale = typeof options.allowStale === 'boolean'
      ? options.allowStale
      : this.allowStale

    if (!allowStale && (Date.now() - this.#maxAge) > entry.modifiedAt) {
      this.delete(entry.key)
      this.#emit('expired', entry)
      return this.#notFoundReturnValue
    }

    super.delete(key)
    super.set(key, entry)
    return entry.value
  }

  /**
   * Returns an iterable object yielding the keys in the cache.
   * In order from most recently used to least recently used.
   * @returns {IterableIterator<any>}
   */
  keys () {
    const keys = [...super.keys()]
    let index = keys.length

    const res = {
      * [Symbol.iterator] () {
        while (--index >= 0) {
          yield keys[index]
        }
      }
    }

    return res
  }

  /**
   * Returns the cached value by its key.
   * Does not update order of list.
   * Time complexity: O(1) in average.
   * @param {string} key
   * @param {{ allowStale: boolean } | undefined} options
   * @returns {any}
   */
  peek (key, options = {}) {
    const entry = super.get(key)

    if (entry === undefined) {
      return this.#notFoundReturnValue
    }

    const allowStale = typeof options.allowStale === 'boolean'
      ? options.allowStale
      : this.allowStale

    if (!allowStale && (Date.now() - this.#maxAge) > entry.modifiedAt) {
      this.delete(entry.key)
      this.#emit('expired', entry)
      return this.#notFoundReturnValue
    }

    return entry.value
  }

  /**
   * Sets the value to cache by its key.
   * Time complexity: O(1).
   * @param {string} key
   * @param {any} value
   */
  set (key, value) {
    if (this.size === this.maxSize) {
      const keyIterator = super.keys()
      const { value: headKey } = keyIterator.next()
      this.delete(headKey)
    }

    if (this.has(key)) {
      const entry = super.get(key)
      entry.modifiedAt = Date.now()
      entry.value = value
      super.delete(key)
      super.set(key, entry)
    } else {
      super.set(key, new LRUCacheEntry(key, value))
      const size = this.#sizeCalculation(value, key)

      if (typeof size !== 'number') {
        throw new TypeError('sizeCalculation should return number to be added to size of cache.')
      }

      this.#size += size
    }

    if (this.#useInterval && !this.#interval.isSet && Number.isFinite(this.#maxAge)) {
      this.#interval.set()
    }

    return this
  }

  /**
   * Returns an iterable object yielding the values in the cache.
   * In order from most recently used to least recently used.
   * @returns {IterableIterator<any>}
   */
  values () {
    const entries = [...super.values()]
    let index = entries.length

    const res = {
      * [Symbol.iterator] () {
        while (--index >= 0) {
          yield entries[index].value
        }
      }
    }

    return res
  }
}

export function toRawPrecision (x, minPrecision, maxPrecision) {
  const p = maxPrecision
  let m
  let e
  let xFinal
  if (x === 0) {
    m = repeat('0', p)
    e = 0
    xFinal = 0
  } else {
    const xToString = x.toString()
    // If xToString is formatted as scientific notation, the number is either very small or very
    // large. If the precision of the formatted string is lower that requested max precision, we
    // should still infer them from the formatted string, otherwise the formatted result might have
    // precision loss (e.g. 1e41 will not have 0 in every trailing digits).
    const xToStringExponentIndex = xToString.indexOf('e')
    const [xToStringMantissa, xToStringExponent] = xToString.split('e')
    const xToStringMantissaWithoutDecimalPoint = xToStringMantissa.replace(
      '.',
      ''
    )

    if (
      xToStringExponentIndex >= 0 &&
      xToStringMantissaWithoutDecimalPoint.length <= p
    ) {
      e = +xToStringExponent
      m =
        xToStringMantissaWithoutDecimalPoint +
        repeat('0', p - xToStringMantissaWithoutDecimalPoint.length)
      xFinal = x
    } else {
      e = getMagnitude(x)

      const decimalPlaceOffset = e - p + 1
      // n is the integer containing the required precision digits. To derive the formatted string,
      // we will adjust its decimal place in the logic below.
      let n = Math.round(adjustDecimalPlace(x, decimalPlaceOffset))

      // The rounding caused the change of magnitude, so we should increment `e` by 1.
      if (adjustDecimalPlace(n, p - 1) >= 10) {
        e = e + 1
        // Divide n by 10 to swallow one precision.
        n = Math.floor(n / 10)
      }

      m = n.toString()
      // Equivalent of n * 10 ** (e - p + 1)
      xFinal = adjustDecimalPlace(n, p - 1 - e)
    }
  }
  let int
  if (e >= p - 1) {
    m = m + repeat('0', e - p + 1)
    int = e + 1
  } else if (e >= 0) {
    m = `${m.slice(0, e + 1)}.${m.slice(e + 1)}`
    int = e + 1
  } else {
    m = `0.${repeat('0', -e - 1)}${m}`
    int = 1
  }
  if (m.indexOf('.') >= 0 && maxPrecision > minPrecision) {
    let cut = maxPrecision - minPrecision
    while (cut > 0 && m[m.length - 1] === '0') {
      m = m.slice(0, -1)
      cut--
    }
    if (m[m.length - 1] === '.') {
      m = m.slice(0, -1)
    }
  }
  return { formattedString: m, roundedNumber: xFinal, integerDigitsCount: int }

  // x / (10 ** magnitude), but try to preserve as much floating point precision as possible.
  function adjustDecimalPlace (x, magnitude) {
    return magnitude < 0 ? x * 10 ** -magnitude : x / 10 ** magnitude
  }
}

/**
 * https://www.ecma-international.org/ecma-262/11.0/index.html#sec-samevalue
 * @param x
 * @param y
 */
export function isSameValue (x, y) {
  if (Object.is) {
    return Object.is(x, y)
  }
  // isSameValue algorithm
  if (x === y) {
    // Steps 1-5, 7-10
    // Steps 6.b-6.e: +0 != -0
    return x !== 0 || 1 / x === 1 / y
  }
  // Step 6.a: NaN == NaN
  return x !== x && y !== y
}

/**
 * Cannot do Math.log(x) / Math.log(10) bc if IEEE floating point issue
 * @param x number
 */
export function getMagnitude (x) {
  // Cannot count string length via Number.toString because it may use scientific notation
  // for very small or very large numbers.
  return Math.floor(Math.log(x) * Math.LOG10E)
}

export function repeat (s, times) {
  if (typeof s.repeat === 'function') {
    return s.repeat(times)
  }
  const arr = new Array(times)
  for (let i = 0; i < arr.length; i++) {
    arr[i] = s
  }
  return arr.join('')
}

/**
 * https://tc39.es/ecma402/#sec-formatnumberstring
 */
export function formatNumericToString (intlObject, x) {
  const isNegative = x < 0 || isSameValue(x, -0)
  if (isNegative) {
    x = -x
  }

  let result

  const rourndingType = intlObject.roundingType

  switch (rourndingType) {
    case 'significantDigits':
      result = toRawPrecision(
        x,
        intlObject.minimumSignificantDigits,
        intlObject.maximumSignificantDigits
      )
      break
    case 'fractionDigits':
      result = toRawFixed(
        x,
        intlObject.minimumFractionDigits,
        intlObject.maximumFractionDigits
      )
      break
    default:
      result = toRawPrecision(x, 1, 2)
      if (result.integerDigitsCount > 1) {
        result = toRawFixed(x, 0, 0)
      }
      break
  }

  x = result.roundedNumber
  let string = result.formattedString
  const int = result.integerDigitsCount
  const minInteger = intlObject.minimumIntegerDigits

  if (int < minInteger) {
    const forwardZeros = repeat('0', minInteger - int)
    string = forwardZeros + string
  }

  if (isNegative) {
    x = -x
  }
  return { roundedNumber: x, formattedString: string }
}

/**
 * TODO: dedup with intl-pluralrules and support BigInt
 * https://tc39.es/ecma402/#sec-torawfixed
 * @param x a finite non-negative Number or BigInt
 * @param minFraction and integer between 0 and 20
 * @param maxFraction and integer between 0 and 20
 */
export function toRawFixed (x, minFraction, maxFraction) {
  const f = maxFraction
  const n = Math.round(x * 10 ** f)
  const xFinal = n / 10 ** f

  // n is a positive integer, but it is possible to be greater than 1e21.
  // In such case we will go the slow path.
  // See also: https://tc39.es/ecma262/#sec-numeric-types-number-tostring
  let m
  if (n < 1e21) {
    m = n.toString()
  } else {
    m = n.toString()
    const [mantissa, exponent] = m.split('e')
    m = mantissa.replace('.', '')
    m = m + repeat('0', Math.max(+exponent - m.length + 1, 0))
  }
  let int
  if (f !== 0) {
    let k = m.length
    if (k <= f) {
      const z = repeat('0', f + 1 - k)
      m = z + m
      k = f + 1
    }
    const a = m.slice(0, k - f)
    const b = m.slice(k - f)
    m = `${a}.${b}`
    int = a.length
  } else {
    int = m.length
  }
  let cut = maxFraction - minFraction
  while (cut > 0 && m[m.length - 1] === '0') {
    m = m.slice(0, -1)
    cut--
  }
  if (m[m.length - 1] === '.') {
    m = m.slice(0, -1)
  }
  return { formattedString: m, roundedNumber: xFinal, integerDigitsCount: int }
}

const CHARS = [
  ' ',
  '!',
  '"',
  '#',
  '$',
  '%',
  '&',
  '(',
  ')',
  '*',
  '+',
  ',',
  '-',
  '.',
  '/',
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  ':',
  ';',
  '<',
  '=',
  '>',
  '?',
  '@',
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'W',
  'X',
  'Y',
  'Z',
  '[',
  ']',
  '^',
  '_',
  '`',
  'a',
  'b',
  'c',
  'd',
  'e',
  'f',
  'g',
  'h',
  'i',
  'j',
  'k',
  'l',
  'm',
  'n',
  'o',
  'p',
  'q',
  'r',
  's',
  't',
  'u',
  'v',
  'w',
  'x',
  'y',
  'z',
  '{',
  '|',
  '}',
  '~',
  '¡',
  '¢',
  '£',
  '¥',
  '§',
  '¨',
  '©',
  'ª',
  '¬',
  '®',
  '¯',
  '°',
  '±',
  'µ',
  '¶',
  '·',
  'º',
  '»',
  '¼',
  '½',
  '¾',
  '¿',
  'À',
  'Á',
  'Â',
  'Ã',
  'Ä',
  'Å',
  'Æ',
  'Ç',
  'È',
  'É',
  'Ê',
  'Ë',
  'Ì',
  'Í',
  'Î',
  'Ï',
  'Ñ',
  'Ò',
  'Ó',
  'Ô',
  'Õ',
  'Ö',
  '×',
  'Ø',
  'Ù',
  'Ú',
  'Û',
  'Ü',
  'Þ',
  'ß',
  'à',
  'á',
  'â',
  'ã',
  'ä',
  'å',
  'æ',
  'ç',
  'è',
  'é',
  'ê',
  'ë',
  'ì',
  'í',
  'î',
  'ï',
  'ð',
  'ñ',
  'ò',
  'ó',
  'ô',
  'õ',
  'ö',
  'ø',
  'ù',
  'ú',
  'û',
  'ü',
  'þ',
  'ÿ',
  'Ā',
  'ā',
  'Ć',
  'ć',
  'Č',
  'č',
  'Ē',
  'ē',
  'Ė',
  'ė',
  'Ę',
  'ę',
  'Ħ',
  'Ī',
  'ī',
  'Į',
  'į',
  'ı',
  'Ł',
  'ł',
  'Ń',
  'ń',
  'Ō',
  'ō',
  'Œ',
  'œ',
  'Ś',
  'ś',
  'Š',
  'š',
  'Ū',
  'ū',
  'Ÿ',
  'Ź',
  'ź',
  'Ż',
  'ż',
  'Ž',
  'ž',
  'ƒ',
  'ˆ',
  'ˇ',
  '˛',
  'Ͳ',
  'Θ',
  'Λ',
  'Ξ',
  'Φ',
  'Ψ',
  'Ω',
  'ή',
  'π',
  'Ϟ',
  'ϡ',
  'Ϣ',
  'Ϧ',
  'ϫ',
  'ϰ',
  'ϱ',
  'Ͼ',
  'Ⴃ',
  'Ⴟ',
  '“',
  '”',
  '„',
  '†',
  '‡',
  '•',
  '…',
  '™',
  '↵',
  '∂',
  '∆',
  '∏',
  '∑',
  '√',
  '∞',
  '∫',
  '≈',
  '≠',
  '≤'
]

export function encode256 (view) {
  let res = ''
  let index = -1
  const { length } = view

  while (++index < length) {
    res += CHARS[view[index]]
  }

  return res
}

export function decode256 (string) {
  let index = -1
  const { length } = string
  const view = ByteView.alloc(length)

  while (++index < length) {
    view[index] = CHARS.indexOf(string[index])
  }

  return view
}

export function getArrayOption (opts, prop, values, fallback) {
  if (typeof opts !== 'object') {
    throw new TypeError('Options must be an object')
  }

  values = values !== undefined ? new Set(values) : values
  let value = opts[prop]
  if (value !== undefined) {
    if (values !== undefined) {
      for (const v of value) {
        if (!values.has(v)) {
          throw new RangeError(`${v} is not within [${[...values].join(', ')}]`)
        }
      }
    }

    return value
  }

  return fallback
}
